/* eslint-disable camelcase, no-use-before-define */
import URI from 'urijs';
import _ from 'lodash';

import AppDispatcher from '../dispatcher/AppDispatcher';
import {ActionTypes, LoadingTypes} from '../constants/AppConstants';
import config from '../config';
import LinkMapper, {resolve} from '../helpers/LinkMapper';
import { trimPayload } from '../helpers/RequestBody';
import LoadingActions from '../actions/LoadingActions';

import TraceToken from '../helpers/TraceToken';
import { URL } from '../helpers/CustomerEndpoint';

import { RequestError, ServerError, BadRequestError, UnauthorisedError, ForbiddenError, NotFoundError } from '../errors/API';
import AuthToken from "../records/customer/AuthToken";
import UserStore from "../stores/UserStore";
import { logoutUser } from "../decorators/Customer";
import { RefreshTokenError } from '../errors/RefreshTokenError';

//Re-export the errors to make it easy to import with the APIClient
export { RequestError, ServerError, BadRequestError, UnauthorisedError, ForbiddenError, NotFoundError };

let refreshIterator = 0;
let requestsQueue = [];

const resetRefreshActions = () => {
    requestsQueue = [];
    refreshIterator = 0;
};

const withNewToken = (options, refreshToken) => {
    return {
        ...options,
        headers: {
            ...options.headers,
            'x-customer-token': refreshToken
        },
        _isRetry: true
    };
};

const addLinks = function (response) {
    if (_.has(response, 'links')) {
        LinkMapper.addLinks(response.links);
    }
    return response;
};

async function resolveBody (response, options = {}) {
    if (options.responseType === 'arraybuffer') {
        return await response.arrayBuffer();
    } else if (options.responseType === 'blob') {
        return await response.blob();
    } else if (options.responseType === 'text') {
        return await response.text();
    }

    const contentType = response.headers.get('content-type') || 'text/plain';
    let body;

    if (response.status === 204) {
        body = '';
    } else if (contentType.indexOf('application/json') > -1) {
        body = await response.json();
        body = addLinks(body);
    } else if (contentType.includes('image/')) {
        body = await response.blob();
    } else {
        body = await response.text();
    }

    return body;
}

function createErrorFrom (status, response, resolvedBody, errorContext) {
    const errors = {
        500: ServerError,
        400: BadRequestError,
        401: UnauthorisedError,
        403: ForbiddenError,
        404: NotFoundError
    };

    let ErrorClass = RequestError;
    if (errors[status]) {
        ErrorClass = errors[status];
    }

    return new ErrorClass(response, resolvedBody, errorContext);
}

export const fetcher = async (url, options) => {
    options.responseMap = options.responseMap || _.identity;
    options.credentials = options.credentials || 'include';
    const id = _.uniqueId('api-');
    const showLoader = options.loader === undefined ? true : options.loader;

    if (showLoader) {
        LoadingActions.start(LoadingTypes.API, id, { url });
    }

    try {
        const response = fetch && await window.fetch(url, options); // hack: allows monkey-patching fetch (kakapo)
        const resolvedBody = await resolveBody(response, options);

        if (!response || !response.ok) {

            if (showLoader) {
                LoadingActions.fail(LoadingTypes.API, id, { url, response });
            }

            if (!options._isRetry && UserStore.getCurrent().refreshToken && response.status === 401 && url.includes(makeUrl('/'))) {
                if (url.includes(URL.refresh)) {
                    resetRefreshActions();
                    logoutUser(response);
                    throw new RefreshTokenError();
                } else {

                    refreshIterator++;

                    if (refreshIterator === 1) {
                        const refreshTokenPayload = {
                            grant_type: 'refresh_token',
                            token: UserStore.getCurrent().refreshToken,
                            session_short: config.get('sessionShort'),
                        };

                        const authResponse = await new APIClient().post(URL.refresh, refreshTokenPayload);
                        const token = resolve(authResponse.result).as(AuthToken);

                        AppDispatcher.dispatch({
                            actionType: ActionTypes.AUTH_REFRESHED,
                            token
                        });

                        if (token) {
                            const result = await fetcher(url, withNewToken(options, token.accessToken));
                            requestsQueue.map((resolver => resolver(token.accessToken)));
                            resetRefreshActions();

                            return result;
                        } else {
                            resetRefreshActions();
                            logoutUser(response);
                            throw new RefreshTokenError();
                        }
                    } else {
                        // eslint-disable-next-line no-shadow
                        return new Promise((resolve, reject) => {
                            requestsQueue.push(async (token) => {
                                try {
                                    const result = await fetcher(url, withNewToken(options, token));
                                    resolve(result);
                                } catch (error) {
                                    reject(error);
                                }
                            });
                        });
                    }
                }
            } else if (!refreshIterator) {
                const errorContext = {
                    url,
                    method: (options.method || 'GET').toUpperCase(),
                    status: response && response.status,
                    traceToken: options.headers['x-trace-token'],
                    body: JSON.stringify(resolvedBody)
                };
                throw createErrorFrom(response.status, response, resolvedBody, errorContext);
            }
        }

        if (showLoader) {
            LoadingActions.success(LoadingTypes.API, id, { url, response });
        }

        return options.responseMap(resolvedBody, response);
    } catch (error) {
        const shouldSuppressThrow = options.validErrorStatuses && error.extra && options.validErrorStatuses.includes(error.extra.status);

        if (showLoader) {
            LoadingActions.fail(LoadingTypes.API, id, { url, error });
        }

        if (!shouldSuppressThrow) {
            throw error;
        }

        return error;
    }
};

const needsCorsProxy = () => config.get('forceCORSProxy') || !('withCredentials' in new XMLHttpRequest());

export const makeUrl = (path, options = {}) => {
    const defaults = {
        protocol: config.get('apiProtocol'),
        apiHost: config.get('apiHost'),
        apiPort: config.get('apiPort'),
        apiPath: config.get('apiPath')
    };
    options = _.defaults(options, defaults);
    return URI.build({
        protocol: options.protocol,
        hostname: needsCorsProxy() ? window.location.hostname : options.apiHost,
        port: needsCorsProxy() ? window.location.port : options.apiPort,
        path: options.apiPath + path,
        query: options.query
    });
};

function makeRequestBody (data, options = {}) {
    return JSON.stringify(options.trim ? trimPayload(data) : data);
}

export class APIClient {

    makeUrl (...args) { return makeUrl(...args); }

    getHeaders () {
        const clientId = `${config.get('name')}@${config.get('version')}`;
        let headers = {
            'accept': 'application/json',
            'content-type': 'application/json',
            'x-access-token': config.get('apiAccessToken'),
            'x-trace-token': `${clientId}/${TraceToken.getToken()}`
        };

        if (needsCorsProxy()) {
            headers['x-cors-proxy'] = 1;
        }

        return headers;
    }

    post (path, data = {}, options = {}) {
        options = Object.assign({}, options, {
            method: 'POST',
            headers: { ...this.getHeaders(), ...options.headers },
            body: makeRequestBody(data, options)
        });
        return fetcher(this.makeUrl(path, options), options);
    }

    postForm (path, data = {}, files = {}) {
        let headers = this.getHeaders();
        let body = new FormData();
        _.forEach(data, (v, k) => body.append(k, v));
        _.forEach(files, (v, k) => {
            if (v instanceof Blob) {
                body.append(k, v, 'blob.jpg');
            } else {
                body.append(k, v);
            }
        });
        delete headers['content-type'];

        return fetcher(this.makeUrl(path), {
            method: 'POST',
            headers: headers,
            body: body
        });
    }

    get (path, data = {}, options = {}) {
        options = Object.assign({}, options, {
            method: 'GET',
            headers: { ...this.getHeaders(), ...options.headers },
        });
        const url = this.makeUrl(path, { query: URI.buildQuery(data) });
        return fetcher(url, options);
    }

    put (path, data = {}, options = {}) {
        options = Object.assign({}, options, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: makeRequestBody(data, options)
        });
        return fetcher(this.makeUrl(path), options);
    }

    delete (path, data = null, options = {}) {
        options = Object.assign({}, options, {
            method: 'DELETE',
            headers: this.getHeaders(),
            body: data && makeRequestBody(data, options)
        });
        return fetcher(this.makeUrl(path), options);
    }

    patch (path, data = {}, options = {}) {
        // fetch fails in chrome ATM
        const { xhr, promise } = this.xhr('PATCH', path);
        xhr.send(makeRequestBody(data, options));
        return promise.then(
            ({ responseText }) => responseText && JSON.parse(responseText)
        );
    }

    xhr (method, path, headers = {}) {
        const xhr = new XMLHttpRequest();
        const url = this.makeUrl(path);
        method = method.toUpperCase();
        xhr.open(method, url);
        xhr.withCredentials = true;

        const normalizeHeader = (value, name) => name.toLowerCase();
        headers = Object.assign(
            _.mapKeys(this.getHeaders(), normalizeHeader),
            _.mapKeys(headers, normalizeHeader)
        );
        for (const [name, value] of Object.entries(headers)) {
            if (value !== undefined) {
                xhr.setRequestHeader(name, value);
            }
        }

        // eslint-disable-next-line no-shadow
        const promise = new Promise((resolve, reject) => {
            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHttpRequest.DONE) {
                    const { status, responseText } = xhr;
                    if (status >= 200 && status <= 299) {
                        resolve(xhr);
                    } else {
                        const extraInfo = {
                            path, method, status, responseText
                        };
                        reject(createErrorFrom(status, xhr, responseText, extraInfo));
                    }
                }
            };
        });
        return { xhr, promise };
    }
}

export default new APIClient();



// WEBPACK FOOTER //
// ./src/lib/APIClient.js